import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Heart, Check, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { copy } from '@/copy';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('verifying');

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }
    const verify = async () => {
      try {
        await axios.get(`${API}/api/auth/verify-email/${token}`);
        setStatus('success');
      } catch (err) {
        const detail = err.response?.data?.detail || '';
        setStatus(detail.includes('expired') ? 'expired' : 'invalid');
      }
    };
    verify();
  }, [token]);

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex flex-col">
      <header className="px-4 py-6 border-b border-[#E5E5E5]">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="font-serif text-lg text-[#1D2D44]">
            Our Love <Heart className="inline-block w-4 h-4 text-[#C9A84C] mx-0.5" strokeWidth={1.5} /> Connection
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md border-none shadow-lg rounded-2xl overflow-hidden" data-testid="verify-email-card">
          <div className="h-1 bg-[#B89B5E]"></div>
          <CardContent className="p-8 text-center">

            {status === 'verifying' && (
              <div data-testid="verify-loading">
                <div className="w-8 h-8 border-2 border-[#1D2D44] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-[#1D2D44]">{copy.verification.verifying}</p>
              </div>
            )}

            {status === 'success' && (
              <div className="animate-fade-in" data-testid="verify-success">
                <div className="w-12 h-12 bg-[#5E7F63]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-6 h-6 text-[#5E7F63]" />
                </div>
                <h2 className="font-serif text-2xl text-[#1D2D44] mb-3">{copy.verification.successTitle}</h2>
                <p className="text-[#3A4A5A] text-sm mb-6">{copy.verification.successMessage}</p>
                <Link to="/login">
                  <Button className="bg-[#1D2D44] text-[#B89B5E] hover:bg-[#1F1F1F] rounded-full px-8" data-testid="verify-login-btn">
                    {copy.verification.signIn}
                  </Button>
                </Link>
              </div>
            )}

            {(status === 'invalid' || status === 'expired') && (
              <div className="animate-fade-in" data-testid="verify-failed">
                <div className="w-12 h-12 bg-[#C9A84C]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-6 h-6 text-[#C9A84C]" />
                </div>
                <h2 className="font-serif text-2xl text-[#1D2D44] mb-3">
                  {status === 'expired' ? copy.verification.expiredTitle : copy.verification.invalidTitle}
                </h2>
                <p className="text-[#3A4A5A] text-sm mb-6">
                  {status === 'expired' ? copy.verification.expiredMessage : copy.verification.invalidMessage}
                </p>
                <Link to="/login">
                  <Button className="bg-[#1D2D44] text-[#B89B5E] hover:bg-[#1F1F1F] rounded-full px-8" data-testid="verify-back-btn">
                    {copy.verification.backToLogin}
                  </Button>
                </Link>
              </div>
            )}

          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default VerifyEmailPage;
